// Global packages and components
import { useState, useRef } from 'react'
import { clsx } from 'clsx'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { useTruncated } from '~/hooks'
import AnimateHeight from 'react-animate-height'

// Types
import type { ReactNode } from 'react'

interface Props {
  label: string
  children: ReactNode
  defaultOpen?: boolean
  hasDivider?: boolean
}

// Main export
const SidebarFilter = ({
  label,
  children,
  defaultOpen = true,
  hasDivider = true,
}: Props) => {
  const ref = useRef(null)
  const [open, setOpen] = useState<boolean>(defaultOpen)
  const { isTruncated, isShowingMore, toggleIsShowingMore, shouldTruncate } =
    useTruncated({
      ref: ref,
    })

  return (
    <>
      <div className="mb-6">
        <button
          onClick={() => setOpen(!open)}
          className="flex w-full items-center justify-between"
        >
          <span className="font-semibold">{label}</span>
          <ChevronDownIcon
            className={clsx('size-6', {
              'rotate-180': open,
            })}
          />
        </button>
        <AnimateHeight
          duration={300}
          height={open ? 'auto' : 0}
          onHeightAnimationEnd={() => {
            shouldTruncate()
          }}
          animateOpacity
        >
          <div
            ref={ref}
            className={clsx('pt-4', {
              'max-h-48 overflow-hidden': !isShowingMore,
              'mb-3': isTruncated,
            })}
          >
            {children}
          </div>
          {isTruncated && (
            <button
              onClick={toggleIsShowingMore}
              className="font-semibold text-teal-green underline"
            >
              {isShowingMore ? 'See less' : 'See more'}
            </button>
          )}
        </AnimateHeight>
      </div>
      {hasDivider && <hr className="mb-6 border-neutral-200" />}
    </>
  )
}

export default SidebarFilter
